/* Отчёты. ТЗ 6.7 — ежедневный по участникам и еженедельный по бизнесам. */

import { humanDate } from "@mera/shared";
import { Button, List, ListInput, Sheet, Toolbar } from "konsta/react";
import React, { useState } from "react";

import { Ion } from "../components/icons.jsx";
import Screen from "../components/Screen.jsx";
import { SkelReports } from "../components/skeleton.jsx";
import { Chips, Empty, ErrorState, Eyebrow, Hint, Sep } from "../components/ui.jsx";
import { api } from "../lib/api.js";
import { useApp, useFetch } from "../lib/store.jsx";
import { closeApp, haptic, inTelegram } from "../lib/tg.js";

export default function Reports() {
  const [tab, setTab] = useState("daily");
  const [formOpen, setFormOpen] = useState(false);
  const [err, setErr] = useState(null);
  const daily = useFetch("/reports/daily");
  const weekly = useFetch("/reports/weekly");

  /* Отчёт наговаривают боту: просим прислать памятку и уходим из мини-аппа.
     Вне Telegram закрывать нечего — там остаётся форма ввода вручную. */
  const toBot = async () => {
    haptic.medium();
    setErr(null);
    try {
      await api.post("/reports/script", { kind: tab });
      if (inTelegram) closeApp();
      else setFormOpen(true);
    } catch (e) {
      haptic.error();
      setErr(e.message);
    }
  };

  return (
    <Screen back>
      <div className="flex items-center gap-2 px-4 py-2">
        <div className="flex gap-2 flex-1">
          {[
            { id: "daily", label: "Ежедневный" },
            { id: "weekly", label: "Еженедельный" },
          ].map((t) => (
            <button
              key={t.id}
              onClick={() => { haptic.select(); setTab(t.id); }}
              className="rounded-full px-3.5 py-1.5 text-[13px] font-semibold active:opacity-70"
              style={{
                background: tab === t.id ? "var(--tg-button)" : "var(--tg-section-bg)",
                color: tab === t.id ? "var(--tg-button-text)" : "var(--tg-text)",
              }}
            >
              {t.label}
            </button>
          ))}
        </div>
        <button
          onClick={toBot}
          className="shrink-0 inline-flex items-center justify-center w-8 h-8 rounded-full active:opacity-70"
          style={{ background: "color-mix(in srgb, var(--tg-button) 14%, transparent)", color: "var(--tg-button)" }}
          aria-label="Сдать отчёт"
        >
          <Ion.add size={16} />
        </button>
      </div>

      {err ? (
        <div className="px-4 pb-1 text-center text-[13px]" style={{ color: "var(--fin-red)" }}>{err}</div>
      ) : null}

      {tab === "daily" ? <Daily q={daily} /> : <Weekly q={weekly} />}

      <DailyForm opened={formOpen} onClose={() => setFormOpen(false)} onSaved={() => { setFormOpen(false); daily.refetch(); }} />
    </Screen>
  );
}

function Daily({ q }) {
  if (q.loading) return <SkelReports />;
  if (q.error) return <ErrorState text={q.error} onRetry={q.refetch} />;
  if (!q.data?.length) return <Empty text="Отчётов пока нет" icon={Ion.mic} />;

  // Группируем по дате
  const byDate = q.data.reduce((acc, r) => {
    (acc[r.date] ||= []).push(r);
    return acc;
  }, {});

  return (
    <>
      {Object.entries(byDate).map(([date, rows]) => (
        <div key={date}>
          <Eyebrow>{humanDate(date)}</Eyebrow>
          <div className="px-4 flex flex-col gap-2">
            {rows.map((r) => (
              <div key={r.id} className="card-tg px-4 py-3">
                <div className="flex items-center gap-2">
                  <span className="text-[15px] font-semibold">{r.who}</span>
                  {r.role ? <Hint>· {r.role}</Hint> : null}
                  {r.voice ? (
                    <span className="ml-auto inline-flex items-center gap-1 text-[12px]" style={{ color: "var(--tg-hint)" }}>
                      <Ion.mic size={11} /> {r.voice}
                    </span>
                  ) : null}
                </div>
                <div className="mt-1.5 text-[14px] leading-snug">{r.summary}</div>
                {r.tomorrow?.length ? (
                  <div className="mt-2">
                    <div className="eyebrow px-0 pt-0 pb-1">Приоритеты на завтра</div>
                    <div className="flex flex-col gap-1">
                      {r.tomorrow.map((t, i) => (
                        <div key={i} className="flex items-start gap-2">
                          <span className="num text-[11px] font-bold mt-0.5" style={{ color: "var(--fin-gold)" }}>
                            {i + 1}
                          </span>
                          <span className="text-[13px] leading-snug">{t}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        </div>
      ))}
    </>
  );
}

function Weekly({ q }) {
  if (q.loading) return <SkelReports />;
  if (q.error) return <ErrorState text={q.error} onRetry={q.refetch} />;
  if (!q.data?.length) return <Empty text="Недельных отчётов нет" />;

  return (
    <>
      {q.data.map((w) => (
        <div key={w.id}>
          <Eyebrow>{w.biz} · неделя с {humanDate(w.weekStart)}</Eyebrow>
          <div className="px-4">
            <div className="card-tg px-4 py-3">
              <div className="text-[14px] leading-snug">{w.summary}</div>
            </div>
            {w.reflections?.length ? (
              <div className="mt-2 card-tg py-1">
                {w.reflections.map((r, i) => (
                  <React.Fragment key={i}>
                    {i > 0 ? <Sep /> : null}
                    <div className="px-4 py-2.5">
                      <div className="text-[14px] font-semibold mb-1">{r.who}</div>
                      <Line label="Сделал" text={r.done} />
                      <Line label="Рефлексия" text={r.reflect} />
                      <Line label="План" text={r.plan} />
                    </div>
                  </React.Fragment>
                ))}
              </div>
            ) : null}
          </div>
        </div>
      ))}
    </>
  );
}

const Line = ({ label, text }) =>
  text ? (
    <div className="flex gap-2 py-0.5">
      <span className="eyebrow px-0 pt-0 pb-0 w-[72px] shrink-0">{label}</span>
      <span className="text-[13px] leading-snug flex-1">{text}</span>
    </div>
  ) : null;

/* Сдать ежедневный отчёт: свободный рассказ → ИИ соберёт сводку и приоритеты */
function DailyForm({ opened, onClose, onSaved }) {
  const { today } = useApp();
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  const submit = async () => {
    if (!text.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await api.post("/reports/daily", { text: text.trim(), date: today });
      haptic.success();
      setText("");
      onSaved();
    } catch (e) {
      haptic.error();
      setErr(e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Sheet opened={opened} onBackdropClick={onClose} className="pb-safe w-full">
      <Toolbar top>
        <div className="flex-1 display text-[17px] font-semibold truncate pr-2">Ежедневный отчёт</div>
        <Button clear onClick={onClose}>Отмена</Button>
      </Toolbar>
      <List strongIos insetIos className="!my-2">
        <ListInput
          label="Как прошёл день"
          type="textarea"
          placeholder="Расскажите своими словами — ИИ соберёт сводку и приоритеты на завтра"
          value={text}
          onChange={(e) => setText(e.target.value)}
          inputClassName="!h-32"
        />
      </List>
      {err ? (
        <div className="px-4 text-center text-[13px]" style={{ color: "var(--fin-red)" }}>{err}</div>
      ) : null}
      <div className="px-4 pb-4 pt-2">
        <Button large onClick={submit} disabled={busy || !text.trim()}>
          <Ion.paperPlane size={15} className="mr-1.5" /> {busy ? "Собираем…" : "Сдать отчёт"}
        </Button>
        <div className="text-center mt-2">
          <Hint>Голосом — через Telegram-бота: наговорите, и он соберёт отчёт сам</Hint>
        </div>
      </div>
    </Sheet>
  );
}
